import { fromNullable } from 'maybeasy';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Droppable } from '@hello-pangea/dnd';
import { ExperienceResourceArray, ProductCollectionResource } from '../../../Types';
import ExperienceSection from '../../ExperienceSection';
import NoExperiences from '../../NoExperiences';
import ProductCollectionDisplay from '../ProductCollectionDisplay';
import ScrollButton from './ScrollButton';
import DraggableExperienceCard from './DraggableExperienceCard';
import { useUnifiedDragContext } from '../UnifiedDragContext';
import EmptyDropZone from '../Shared/EmptyDropZone';
import { useExperienceMap } from '../Shared/hooks/useExperienceMap';
import { getDragHighlightClass } from '../Shared/styles/dragStyles';

const CARD_WIDTH = 300;
// CARD_GAP matches Tailwind's gap-4 class (1rem = 16px at default font size)
const CARD_GAP = 16;
const SCROLL_DISTANCE = CARD_WIDTH + CARD_GAP;

export interface Props {
  productCollection: ProductCollectionResource;
  experiencesArray: ExperienceResourceArray;
  index: number;
  totalSections: number;
  onOpenCatalog: () => void;
  editable?: boolean;
}

function Carousel({
  productCollection,
  experiencesArray,
  index,
  totalSections,
  onOpenCatalog,
  editable = false,
}: Props) {
  const { experiences } = productCollection.payload;
  const { isDragging } = useUnifiedDragContext();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      updateScrollState();
      container.addEventListener('scroll', updateScrollState);
      window.addEventListener('resize', updateScrollState);
      return () => {
        container.removeEventListener('scroll', updateScrollState);
        window.removeEventListener('resize', updateScrollState);
      };
    }
    return undefined;
  }, [updateScrollState, experiences.length]);

  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -SCROLL_DISTANCE, behavior: 'smooth' });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: SCROLL_DISTANCE, behavior: 'smooth' });
    }
  }, []);

  const experienceMap = useExperienceMap(experiencesArray);

  return (
    <ExperienceSection
      productCollection={productCollection}
      index={index}
      totalSections={totalSections}
      onOpenCatalog={onOpenCatalog}
      editable={editable}
    >
      <ProductCollectionDisplay productCollection={productCollection}>
        {experiences.length > 0 ? (
          <div className="mt-8 mb-4 xl:-mx-20">
            <div className="flex w-full items-stretch">
              <div className="hidden xl:flex xl:w-20 flex-shrink-0 items-center justify-center">
                <ScrollButton direction="left" onClick={scrollLeft} disabled={!canScrollLeft} />
              </div>

              <div className="flex-1 overflow-hidden">
                <Droppable droppableId={`section-${index}-carousel`} direction="horizontal">
                  {(provided, snapshot) => (
                    <div
                      ref={(node) => {
                        provided.innerRef(node);
                        scrollContainerRef.current = node;
                      }}
                      {...provided.droppableProps}
                      className={`flex items-stretch gap-4 overflow-x-auto pb-2 transition-colors ${getDragHighlightClass(snapshot.isDraggingOver, false)}`}
                    >
                      {experiences.map((ex, idx) => {
                        const experienceResource = fromNullable(experienceMap[ex]);

                        return (
                          <DraggableExperienceCard
                            key={ex}
                            experienceId={ex}
                            experienceResource={experienceResource}
                            index={idx}
                            sectionIndex={index}
                            isDragging={isDragging}
                            editable={editable}
                          />
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>

              <div className="hidden xl:flex xl:w-20 flex-shrink-0 items-center justify-center">
                <ScrollButton direction="right" onClick={scrollRight} disabled={!canScrollRight} />
              </div>
            </div>
          </div>
        ) : (
          <Droppable droppableId={`section-${index}-carousel`} direction="horizontal">
            {(provided, snapshot) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="mt-8">
                {isDragging ? (
                  <EmptyDropZone isDraggingOver={snapshot.isDraggingOver} />
                ) : (
                  <NoExperiences onOpen={onOpenCatalog} />
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        )}
      </ProductCollectionDisplay>
    </ExperienceSection>
  );
}

export default observer(Carousel);
